import { LEASE_ENDPOINTS, LEASE_DEFAULT_STATUS } from "../constants/leaseEndpoints";

// The lease endpoints answer HTTP 200 with statusType FAILURE/ERROR for business errors
const unwrap = (data) => {
  if (data?.statusType && data.statusType !== "SUCCESS") {
    throw new Error(data.text || "Request failed");
  }
  return data;
};

export const createLeaseService = (httpClient) => {
  return {
    // 1. List the logged-in customer's leases
    listMy: async ({ page_number = 1, page_size = 500, order_by = "DESC" } = {}) => {
      const response = await httpClient.post(LEASE_ENDPOINTS.listMy, {
        page_number,
        page_size,
        order_by,
      });
      return unwrap(response.data);
    },

    // 2. Create (owner is set by the backend from the token; status defaults to PENDING)
    create: async ({ title, description, price, city, state, purpose }) => {
      const response = await httpClient.post(LEASE_ENDPOINTS.create, {
        title,
        description,
        budget: Number(price),
        city,
        state,
        purpose: { id: purpose.id },
        status: LEASE_DEFAULT_STATUS,
      });
      return unwrap(response.data);
    },

    // 3. Edit. The backend saves the purpose object as sent, so the full purpose is passed.
    edit: async ({ id, title, description, price, city, state, purpose }) => {
      const response = await httpClient.put(LEASE_ENDPOINTS.edit, {
        id,
        title,
        description,
        budget: Number(price),
        city,
        state,
        purpose,
      });
      return unwrap(response.data);
    },

    // 3b. One lease (edit page deep links)
    getById: async (id) => {
      const response = await httpClient.get(LEASE_ENDPOINTS.details(id));
      return unwrap(response.data);
    },

    // 4. Status change
    updateStatus: async (leaseId, status) => {
      const response = await httpClient.put(LEASE_ENDPOINTS.updateStatus, {
        lease_id: leaseId,
        status,
      });
      return unwrap(response.data);
    },

    // 5. Delete
    remove: async (id) => {
      const response = await httpClient.delete(LEASE_ENDPOINTS.remove(id));
      return unwrap(response.data);
    },

    // 6. Notes
    listNotes: async (leaseId) => {
      const response = await httpClient.post(LEASE_ENDPOINTS.notes, {
        lead_id: leaseId,
        page_number: 1,
        page_size: 200,
        order_by: "ASC",
      });
      return unwrap(response.data);
    },

    addNote: async (leaseId, note) => {
      const response = await httpClient.post(LEASE_ENDPOINTS.addNote, {
        lease: { id: leaseId },
        note,
        parent_id: 0,
      });
      return unwrap(response.data);
    },

    editNote: async (noteId, note) => {
      const response = await httpClient.put(LEASE_ENDPOINTS.editNote, { id: noteId, note });
      return unwrap(response.data);
    },

    removeNote: async (noteId) => {
      const response = await httpClient.delete(LEASE_ENDPOINTS.removeNote(noteId));
      return unwrap(response.data);
    },

    // 7. Dropdown data
    fetchPurposes: async () => {
      try {
        const response = await httpClient.post(LEASE_ENDPOINTS.purposes, {
          page_number: 1,
          page_size: 100,
          order_by: "ASC",
        });
        return response.data?.leasePurposes || [];
      } catch {
        return [];
      }
    },

    fetchStates: async (countryId = 1) => {
      try {
        const response = await httpClient.post(LEASE_ENDPOINTS.states(countryId), {
          page_number: 1,
          page_size: 100,
          order_by: "ASC",
        });
        return response.data?.states || [];
      } catch {
        return [];
      }
    },

    fetchCities: async (stateId) => {
      try {
        const response = await httpClient.post(LEASE_ENDPOINTS.cities(stateId), {
          page_number: 1,
          page_size: 200,
          order_by: "ASC",
        });
        return response.data?.cities || [];
      } catch {
        return [];
      }
    },
  };
};
